/**
 * BidRequestDrawer Component
 * Create / Edit Bid Request - large drawer (multi-section)
 * BuilderBel Construction Project Management System
 */

import { AlertCircle, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  FormSwitch,
  Input,
  TextArea,
} from '../forms';
import type {
  BidDocument,
  BidInvitation,
  BidRequest,
  CreateBidRequestRequest,
} from '../../types/bidRequest';
import {
  useCreateBidRequestMutation,
  useUpdateBidRequestMutation,
} from '../../services/bidRequestApi';
import { useToast } from '../../contexts/ToastContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const COMMON_TRADES = [
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Plumbing', label: 'Plumbing' },
  { value: 'HVAC', label: 'HVAC' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Framing', label: 'Framing' },
  { value: 'Roofing', label: 'Roofing' },
  { value: 'Drywall', label: 'Drywall' },
  { value: 'Painting', label: 'Painting' },
  { value: 'Flooring', label: 'Flooring' },
  { value: 'Masonry', label: 'Masonry' },
  { value: 'Landscaping', label: 'Landscaping' },
  { value: 'Fire Protection', label: 'Fire Protection' },
  { value: 'Elevators', label: 'Elevators' },
  { value: 'Steel / Structural', label: 'Steel / Structural' },
  { value: 'Demolition', label: 'Demolition' },
  { value: 'Excavation', label: 'Excavation' },
  { value: 'General Conditions', label: 'General Conditions' },
  { value: 'Other', label: 'Other' },
];

// ============================================================================
// HELPERS
// ============================================================================

const formatDateForInput = (dateStr?: string): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
};

// ============================================================================
// PROPS
// ============================================================================

interface BidRequestDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  bidRequest?: BidRequest | null;
}

// ============================================================================
// EMPTY DEFAULTS
// ============================================================================

const emptyInvitation = (): Omit<BidInvitation, 'status'> & { status: 'pending' } => ({
  vendorId: '',
  vendorName: '',
  vendorEmail: '',
  status: 'pending',
});

const emptyDocument = (): BidDocument => ({
  fileName: '',
  fileUrl: '',
  fileType: '',
});

// ============================================================================
// COMPONENT
// ============================================================================

const BidRequestDrawer: React.FC<BidRequestDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  bidRequest,
}) => {
  const toast = useToast();
  const isEditing = !!bidRequest;

  const [createBidRequest, { isLoading: isCreating }] = useCreateBidRequestMutation();
  const [updateBidRequest, { isLoading: isUpdating }] = useUpdateBidRequestMutation();
  const isSaving = isCreating || isUpdating;

  // ---- Form State ----
  const [name, setName] = useState<string>('');
  const [trade, setTrade] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [scopeOfWork, setScopeOfWork] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [walkThroughDate, setWalkThroughDate] = useState<string>('');
  const [bondRequired, setBondRequired] = useState<boolean>(false);
  const [insuranceRequirements, setInsuranceRequirements] = useState<string>('');
  const [budgetAmount, setBudgetAmount] = useState<number | undefined>(undefined);

  // Arrays
  const [qualifications, setQualifications] = useState<string[]>([]);
  const [invitations, setInvitations] = useState<
    Array<{ vendorId: string; vendorName: string; vendorEmail: string }>
  >([]);
  const [documents, setDocuments] = useState<BidDocument[]>([]);

  // Collapsible sections
  const [showScope, setShowScope] = useState(true);
  const [showRequirements, setShowRequirements] = useState(false);
  const [showInvitations, setShowInvitations] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when drawer opens or bidRequest changes
  useEffect(() => {
    if (isOpen) {
      setName(bidRequest?.name || '');
      setTrade(bidRequest?.trade || '');
      setDescription(bidRequest?.description || '');
      setScopeOfWork(bidRequest?.scopeOfWork || '');
      setDueDate(formatDateForInput(bidRequest?.dueDate));
      setWalkThroughDate(formatDateForInput(bidRequest?.walkThroughDate));
      setBondRequired(bidRequest?.bondRequired ?? false);
      setInsuranceRequirements(bidRequest?.insuranceRequirements || '');
      setBudgetAmount(bidRequest?.budgetAmount);
      setQualifications(bidRequest?.qualifications?.length ? [...bidRequest.qualifications] : []);
      setInvitations(
        bidRequest?.invitations?.length
          ? bidRequest.invitations.map(inv => ({
              vendorId: inv.vendorId,
              vendorName: inv.vendorName,
              vendorEmail: inv.vendorEmail,
            }))
          : []
      );
      setDocuments(
        bidRequest?.documents?.length ? [...bidRequest.documents] : []
      );
      setErrors({});
    }
  }, [isOpen, bidRequest]);

  // --------------------------------------------------------------------------
  // VALIDATION
  // --------------------------------------------------------------------------

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Bid request name is required';
    if (!trade.trim()) newErrors.trade = 'Trade is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: CreateBidRequestRequest = {
      name: name.trim(),
      trade: trade.trim(),
      description: description.trim() || undefined,
      scopeOfWork: scopeOfWork.trim() || undefined,
      dueDate: dueDate || undefined,
      walkThroughDate: walkThroughDate || undefined,
      bondRequired,
      insuranceRequirements: insuranceRequirements.trim() || undefined,
      budgetAmount,
      qualifications: qualifications.filter(q => q.trim()),
      invitations: invitations.filter(inv => inv.vendorName.trim() && inv.vendorEmail.trim()),
      documents: documents.filter(d => d.fileName.trim() && d.fileUrl.trim()),
    };

    try {
      if (isEditing && bidRequest) {
        await updateBidRequest({
          projectId,
          bidRequestId: bidRequest._id,
          data: payload,
        }).unwrap();
        toast.success('Bid request updated successfully');
      } else {
        await createBidRequest({ projectId, data: payload }).unwrap();
        toast.success('Bid request created successfully');
      }
      onClose();
    } catch {
      toast.error(
        isEditing ? 'Failed to update bid request' : 'Failed to create bid request'
      );
    }
  };

  // --------------------------------------------------------------------------
  // ARRAY HANDLERS
  // --------------------------------------------------------------------------

  const addQualification = () => setQualifications(prev => [...prev, '']);
  const removeQualification = (idx: number) =>
    setQualifications(prev => prev.filter((_, i) => i !== idx));
  const updateQualification = (idx: number, val: string) =>
    setQualifications(prev => prev.map((q, i) => (i === idx ? val : q)));

  const addInvitation = () =>
    setInvitations(prev => [...prev, { vendorId: '', vendorName: '', vendorEmail: '' }]);
  const removeInvitation = (idx: number) =>
    setInvitations(prev => prev.filter((_, i) => i !== idx));
  const updateInvitation = (
    idx: number,
    key: 'vendorId' | 'vendorName' | 'vendorEmail',
    val: string
  ) =>
    setInvitations(prev => prev.map((inv, i) => (i === idx ? { ...inv, [key]: val } : inv)));

  const addDocument = () => setDocuments(prev => [...prev, emptyDocument()]);
  const removeDocument = (idx: number) =>
    setDocuments(prev => prev.filter((_, i) => i !== idx));
  const updateDocument = (idx: number, key: keyof BidDocument, val: string) =>
    setDocuments(prev => prev.map((d, i) => (i === idx ? { ...d, [key]: val } : d)));

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - large (multi-section) */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right duration-200">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Bid Request' : 'Create Bid Request'}
              </h2>
              {isEditing && bidRequest && (
                <p className="text-sm text-gray-500 mt-0.5">{bidRequest.bidNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* ---- BASIC INFORMATION ---- */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Name */}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Bid Request Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Electrical Package"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Trade */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Trade <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    options={COMMON_TRADES}
                    value={trade}
                    onChange={val => setTrade(val)}
                    placeholder="Select trade"
                  />
                  {errors.trade && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.trade}
                    </p>
                  )}
                </div>

                {/* Budget Amount */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Budget Reference (Internal)
                  </label>
                  <CurrencyInput
                    value={budgetAmount}
                    onChange={val => setBudgetAmount(val)}
                    placeholder="0.00"
                  />
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                  <DatePicker value={dueDate} onChange={setDueDate} />
                </div>

                {/* Walk-Through Date */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Walk-Through Date
                  </label>
                  <DatePicker value={walkThroughDate} onChange={setWalkThroughDate} />
                </div>

                {/* Bond Required */}
                <div className="col-span-2">
                  <FormSwitch
                    id="bond-required"
                    label="Bond Required"
                    checked={bondRequired}
                    onChange={checked => setBondRequired(checked)}
                  />
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Description</label>
                  <TextArea
                    value={description}
                    onChange={setDescription}
                    placeholder="Brief description of the bid package"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* ---- SCOPE OF WORK (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowScope(v => !v)}
              >
                <span>Scope of Work</span>
                {showScope ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showScope && (
                <TextArea
                  value={scopeOfWork}
                  onChange={setScopeOfWork}
                  placeholder="Describe the complete scope of work for this bid package..."
                  rows={6}
                />
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- REQUIREMENTS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowRequirements(v => !v)}
              >
                <span>Requirements & Qualifications</span>
                {showRequirements ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showRequirements && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Insurance Requirements
                    </label>
                    <TextArea
                      value={insuranceRequirements}
                      onChange={setInsuranceRequirements}
                      placeholder="e.g. $2M general liability, workers compensation..."
                      rows={2}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs text-gray-500">
                        Qualifications ({qualifications.length})
                      </label>
                    </div>
                    <div className="space-y-2">
                      {qualifications.map((q, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="flex-1">
                            <Input
                              value={q}
                              onChange={e => updateQualification(idx, e.target.value)}
                              placeholder="e.g. Licensed in state, 5+ years experience"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeQualification(idx)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600 flex-shrink-0"
                            aria-label="Remove qualification"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addQualification}
                      className="mt-2 flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                    >
                      <Plus className="h-4 w-4" />
                      Add Qualification
                    </button>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- VENDOR INVITATIONS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowInvitations(v => !v)}
              >
                <span>Vendor Invitations ({invitations.length})</span>
                {showInvitations ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showInvitations && (
                <div className="space-y-3">
                  {invitations.map((inv, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-lg p-4 relative"
                    >
                      <button
                        type="button"
                        onClick={() => removeInvitation(idx)}
                        className="absolute top-3 right-3 p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                        aria-label="Remove invitation"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Vendor Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={inv.vendorName}
                            onChange={e => updateInvitation(idx, 'vendorName', e.target.value)}
                            placeholder="Company name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={inv.vendorEmail}
                            onChange={e => updateInvitation(idx, 'vendorEmail', e.target.value)}
                            placeholder="contact@vendor.com"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addInvitation}
                    className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Vendor Invitation
                  </button>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- DOCUMENTS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowDocuments(v => !v)}
              >
                <span>Documents ({documents.length})</span>
                {showDocuments ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showDocuments && (
                <div className="space-y-3">
                  {documents.map((doc, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-lg p-4 relative"
                    >
                      <button
                        type="button"
                        onClick={() => removeDocument(idx)}
                        className="absolute top-3 right-3 p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                        aria-label="Remove document"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            File Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={doc.fileName}
                            onChange={e => updateDocument(idx, 'fileName', e.target.value)}
                            placeholder="document.pdf"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            File URL <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={doc.fileUrl}
                            onChange={e => updateDocument(idx, 'fileUrl', e.target.value)}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDocument}
                    className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Document
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : isEditing ? (
                'Update Bid Request'
              ) : (
                'Create Bid Request'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default BidRequestDrawer;
